import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import BookingWidget from "@/components/BookingWidget";
import RoomGallery from "@/components/RoomGallery";
import HeroSlideshow from "@/components/HeroSlideshow";
import SkipToContent from "@/components/SkipToContent";
import SEOHead, { hotelStructuredData, faqStructuredData } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

// Gallery images
import lobbyLounge from "@/assets/lobby-lounge.webp";
import lobbyReception from "@/assets/lobby-reception.webp";
import hallway from "@/assets/hallway.webp";
import roomOverview from "@/assets/room-overview.webp";
import roomBedroom from "@/assets/room-bedroom.webp";
import roomKitchen from "@/assets/room-kitchen.webp";
import roomKitchen2 from "@/assets/room-kitchen-2.webp";
import roomBathroom from "@/assets/room-bathroom.webp";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Star,
  Wifi,
  Car,
  Dumbbell,
  Utensils,
  Wind,
  Shield,
  MapPin,
  Building2,
  Briefcase,
  Heart,
  Quote,
  CheckCircle2,
  ArrowRight,
  Phone,
  Tv,
  Bed,
  Percent,
  Gift,
  Calendar,
  HelpCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Images,
} from "lucide-react";

const galleryImages = [
  { src: lobbyLounge, alt: "Lobby Lounge Area", label: "Lobby Lounge" },
  { src: lobbyReception, alt: "Reception Area", label: "Reception" },
  { src: hallway, alt: "Hotel Hallway", label: "Hallway" },
  { src: roomOverview, alt: "Suite Overview", label: "Suite" },
  { src: roomBedroom, alt: "Bedroom", label: "Bedroom" },
  { src: roomKitchen, alt: "Kitchenette", label: "Kitchen" },
  { src: roomKitchen2, alt: "Kitchen View", label: "Kitchen" },
  { src: roomBathroom, alt: "Bathroom", label: "Bathroom" },
];

const PropertyGallery = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const openLightbox = (index: number) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % galleryImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage - 1 + galleryImages.length) % galleryImages.length);
    }
  };

  return (
    <section className="py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-warm-gold/15 text-harbor-blue border-warm-gold/30 mb-4">
            <Images className="w-4 h-4 mr-2" />
            Photo Gallery
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-6">
            Explore Our Property
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Take a virtual tour of Harbor Heights. From our welcoming lobby to our comfortable suites, see what makes us the perfect home away from home.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryImages.map((image, index) => (
            <motion.button
              key={index}
              type="button"
              aria-label={`View ${image.alt} in full screen`}
              className={cn(
                "relative overflow-hidden rounded-xl cursor-pointer group text-left",
                index === 0 ? "col-span-2 row-span-2" : ""
              )}
              whileHover={{ scale: 1.02 }}
              onClick={() => openLightbox(index)}
            >
              <div className={cn(
                "w-full overflow-hidden",
                index === 0 ? "aspect-square" : "aspect-[4/3]"
              )}>
                <img
                  src={image.src}
                  alt={image.alt}
                  width={index === 0 ? 600 : 300}
                  height={index === 0 ? 600 : 225}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300" aria-hidden="true">
                <span className="text-white font-medium text-sm">{image.label}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {isLightboxOpen && selectedImage !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Close gallery lightbox"
              >
                <X className="w-6 h-6" aria-hidden="true" />
              </button>

              {/* Navigation Buttons */}
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="View previous image"
              >
                <ChevronLeft className="w-8 h-8" aria-hidden="true" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="View next image"
              >
                <ChevronRight className="w-8 h-8" aria-hidden="true" />
              </button>

              {/* Main Image */}
              <motion.div
                key={selectedImage}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="relative max-w-5xl max-h-[80vh] w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={galleryImages[selectedImage].src}
                  alt={galleryImages[selectedImage].alt}
                  className="w-full h-full object-contain rounded-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg">
                  <p className="text-white font-medium text-center">
                    {galleryImages[selectedImage].label}
                  </p>
                </div>
              </motion.div>

              {/* Thumbnails */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2">
                {galleryImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                    className={cn(
                      "w-16 h-12 rounded-md overflow-hidden flex-shrink-0 transition-all duration-200",
                      selectedImage === index
                        ? "ring-2 ring-warm-gold scale-110"
                        : "opacity-50 hover:opacity-100"
                    )}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Index = () => {
  const amenities = [
    { icon: <Wifi className="w-6 h-6" />, name: "High-Speed Wi-Fi", description: "Complimentary throughout property" },
    { icon: <Utensils className="w-6 h-6" />, name: "Full Kitchenette", description: "Microwave, fridge, sink & cabinets" },
    { icon: <Car className="w-6 h-6" />, name: "Free Parking", description: "On-site parking for all guests" },
    { icon: <Dumbbell className="w-6 h-6" />, name: "Fitness Center", description: "24/7 access with modern equipment" },
    { icon: <Tv className="w-6 h-6" />, name: "Flat Screen TV", description: "Cable TV in every room" },
    { icon: <Wind className="w-6 h-6" />, name: "Climate Control", description: "Individual AC/heat in each room" },
  ];

  const roomFeatures = [
    "Full-size bed with quality linens",
    "Complete kitchenette with microwave & refrigerator",
    "Private bathroom with walk-in shower",
    "Spacious dresser storage",
    "Dedicated workspace desk",
    "Flat screen TV with cable",
    "Individual climate control",
    "High-speed Wi-Fi included",
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      role: "Business Traveler",
      content: "Perfect for my 6-week assignment. The full kitchenette saved me so much money, and the staff made it feel like home.",
      rating: 5,
      stayLength: "6 weeks",
    },
    {
      name: "David L.",
      role: "Family Relocation",
      content: "We stayed 3 months while house hunting in Sioux City. Clean, affordable, and everything we needed.",
      rating: 5,
      stayLength: "3 months",
    },
    {
      name: "Jennifer K.",
      role: "Medical Professional",
      content: "Convenient location and all the comforts of home. Great value at $50/night!",
      rating: 5,
      stayLength: "2 months",
    },
    {
      name: "Michael R.",
      role: "Construction Contractor",
      content: "I've stayed at dozens of extended stay hotels. Harbor Heights is hands down the best value. Clean rooms, friendly staff, and a great location.",
      rating: 5,
      stayLength: "8 weeks",
    },
    {
      name: "Emily & Tom W.",
      role: "Traveling Nurses",
      content: "We loved having a real kitchen to cook our meals. The weekly housekeeping was a nice touch, and the WiFi was fast enough for streaming.",
      rating: 5,
      stayLength: "13 weeks",
    },
    {
      name: "Robert H.",
      role: "Corporate Assignment",
      content: "My company books Harbor Heights for all our Sioux City assignments now. Professional, affordable, and hassle-free.",
      rating: 5,
      stayLength: "1 month",
    },
  ];

  const homeFaqs = [
    { question: "What are your nightly and weekly rates?", answer: "Our nightly rate is $50 and weekly rate is $225. No hidden fees or resort charges." },
    { question: "Do rooms have kitchens?", answer: "Yes, every room includes a full kitchenette with microwave, refrigerator, sink, and cabinet storage." },
    { question: "Is parking included?", answer: "Yes, complimentary on-site parking is included with all reservations." },
    { question: "Are pets allowed?", answer: "Yes, we are pet-friendly! Dogs and cats are welcome with a one-time pet fee." },
    { question: "What is the cancellation policy?", answer: "We offer flexible cancellation up to 48 hours before check-in for a full refund." },
    { question: "Is there a minimum stay requirement?", answer: "No minimum stay required. We welcome nightly, weekly, and monthly guests." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Harbor Heights Extended Stay | Affordable Extended Stay Hotel in Sioux City, Iowa"
        description="Experience comfortable extended stay accommodations at Harbor Heights in Sioux City, Iowa. Spacious suites with full kitchens at just $50/night or $225/week. Perfect for business travelers, families, and long-term guests."
        canonical="https://www.extendedstayharborheights.com/"
        type="hotel"
        structuredData={{
          ...hotelStructuredData,
          ...faqStructuredData(homeFaqs)
        }}
      />
      <SkipToContent />
      <Navigation />

      {/* Hero Section */}
      <main id="main-content">
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" aria-label="Welcome to Harbor Heights">
          <HeroSlideshow />

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
            <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
              {/* Trust Badge */}
              <Badge className="bg-warm-gold/20 text-white border-warm-gold/30 backdrop-blur-sm px-4 py-2">
                <Star className="w-4 h-4 fill-warm-gold text-warm-gold mr-2" />
                Affordable Extended Stay in Sioux City
              </Badge>

              {/* Hero Headline */}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight text-balance">
                Your Home Away From Home in Sioux City
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
                Comfortable extended stay rooms with full kitchenettes — just <span className="font-bold text-warm-gold">$50/night</span> or <span className="font-bold text-warm-gold">$225/week</span>. Perfect for business travelers, relocations, and long-term guests.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <Button asChild variant="hero" size="xl">
                  <a href="tel:+17122247003" className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call (712) 224-7003
                  </a>
                </Button>
                <Button asChild variant="outlineLight" size="xl">
                  <Link to="/booking">View Our Room</Link>
                </Button>
              </div>

              {/* Call Encouragement */}
              <p className="text-white/80 text-sm animate-fade-in">
                Speak directly with our concierge for personalized rates and availability
              </p>
            </div>
          </div>

          {/* Scroll Indicator - positioned separately to avoid overlap */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-float z-10">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center p-2">
              <div className="w-1 h-3 bg-white/50 rounded-full" />
            </div>
          </div>
        </section>

      {/* Why Harbor Heights Section */}
      <section className="py-20 lg:py-28 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-6">
              Why Choose Harbor Heights?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Experience the perfect blend of home comfort and hotel convenience, designed for extended stays that feel effortless.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Utensils className="w-8 h-8" />,
                title: "Full Kitchenette",
                description: "Save money with your own microwave, refrigerator, sink, and cabinet storage.",
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Weekly Housekeeping",
                description: "Fresh linens and clean spaces to keep you comfortable all stay long.",
              },
              {
                icon: <MapPin className="w-8 h-8" />,
                title: "Sioux City Location",
                description: "Easy access to local businesses, medical centers, and downtown amenities.",
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: "Unbeatable Value",
                description: "Just $50/night or $225/week. No long-term lease required.",
              },
            ].map((item, index) => (
              <article
                key={index}
                className="group bg-card rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-2 animate-fade-in text-center"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-xl bg-warm-gold/10 flex items-center justify-center text-warm-gold group-hover:scale-110 group-hover:bg-warm-gold group-hover:text-white transition-all duration-300">
                  {item.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-harbor-blue mb-3 group-hover:text-warm-gold transition-colors">
                  {item.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Room Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Gallery */}
            <div className="animate-fade-in">
              <RoomGallery variant="hero" />
            </div>

            {/* Room Details */}
            <div className="space-y-8 animate-fade-in">
              <div>
                <Badge className="bg-warm-gold/15 text-harbor-blue border-warm-gold/30 mb-4">
                  Our Room
                </Badge>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-4">
                  Extended Stay Suite
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Our comfortable rooms are designed for guests who need a home away from home. 
                  Whether you're here for a week or several months, you'll have everything you 
                  need to live comfortably in Sioux City.
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {roomFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-accent/50 hover:bg-accent transition-colors">
                    <CheckCircle2 className="w-5 h-5 text-warm-gold flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Pricing */}
              <div className="bg-gradient-to-r from-harbor-blue to-harbor-blue/90 rounded-2xl p-8 text-white">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                  <div>
                    <p className="text-white/80 mb-2">Starting from</p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-5xl font-bold">$50</span>
                      <span className="text-xl text-white/80">/night</span>
                    </div>
                    <p className="text-warm-gold font-semibold mt-2 text-lg">$225/week — Save $125!</p>
                  </div>
                  <Button asChild variant="hero" size="xl">
                    <Link to="/booking">
                      Book Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-20 lg:py-28 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-6">
              Amenities Designed for Extended Stays
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Everything you need to live, work, and relax comfortably during your stay.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities.map((amenity, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-6 bg-card rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 animate-fade-in group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-warm-gold/10 flex items-center justify-center text-warm-gold group-hover:bg-warm-gold group-hover:text-white transition-all duration-300 flex-shrink-0">
                  {amenity.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-harbor-blue mb-1 group-hover:text-warm-gold transition-colors">
                    {amenity.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{amenity.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhood Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue">
                Conveniently Located in Sioux City
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Harbor Heights puts you close to everything Sioux City has to offer. Easy access to local businesses, medical facilities, shopping, and dining makes your extended stay convenient and comfortable.
              </p>

              <div className="space-y-4">
                {[
                  { icon: <Building2 />, title: "Close to Downtown", description: "Easy access to Sioux City business district" },
                  { icon: <Briefcase />, title: "Near Medical Centers", description: "Convenient to area hospitals and clinics" },
                  { icon: <MapPin />, title: "Local Dining & Shopping", description: "Restaurants, grocery stores, and shopping nearby" },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-accent/50 hover:bg-accent transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-warm-gold flex items-center justify-center text-white flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-harbor-blue mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground/70">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-card-hover animate-fade-in">
              <div className="absolute inset-0 bg-gradient-to-br from-harbor-blue/20 to-warm-gold/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8 bg-white/90 backdrop-blur-sm rounded-xl">
                  <MapPin className="w-16 h-16 text-warm-gold mx-auto mb-4" />
                  <p className="text-xl font-serif font-semibold text-harbor-blue mb-2">Harbor Drive</p>
                  <p className="text-muted-foreground">Sioux City, IA 51101</p>
                  <Button variant="outline" size="lg" className="mt-6" asChild>
                    <Link to="/contact">Get Directions</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 bg-harbor-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Guest Reviews
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              What Our Guests Say
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Don't just take our word for it. Here's what travelers, families, and professionals are saying about Harbor Heights.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-warm-gold text-warm-gold" />
                ))}
              </div>
              <span className="text-xl font-semibold">4.9</span>
              <span className="text-white/80">• {testimonials.length}+ Reviews</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <article
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 space-y-4 hover:bg-white/15 transition-all duration-300 hover:-translate-y-2 animate-fade-in group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between">
                  <Quote className="w-10 h-10 text-warm-gold group-hover:scale-110 transition-transform" />
                  <span className="text-xs bg-white/15 px-3 py-1 rounded-full text-white/90">
                    {testimonial.stayLength} stay
                  </span>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-warm-gold text-warm-gold" />
                  ))}
                </div>
                <p className="text-white/90 leading-relaxed italic">"{testimonial.content}"</p>
                <div className="pt-4 border-t border-white/10">
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-white/80">{testimonial.role}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-white/80 text-sm">
              ⭐ Rated 4.9/5 based on extended stay guest reviews
            </p>
          </div>
        </div>
      </section>

      {/* Property Gallery Section */}
      <PropertyGallery />

      {/* Special Offers / Promotions Section */}
      <section className="py-20 lg:py-28 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-warm-gold/15 text-harbor-blue border-warm-gold/30 mb-4">
              <Gift className="w-4 h-4 mr-2" />
              Special Offers
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-6">
              Extended Stay Savings
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The longer you stay, the more you save. Take advantage of our special rates for weekly and monthly guests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: <Percent className="w-8 h-8" />,
                title: "Weekly Rate",
                price: "$225",
                period: "/week",
                savings: "Save $125 vs nightly rate",
                features: ["7+ night stays", "All amenities included", "Weekly housekeeping"],
                highlight: false,
              },
              {
                icon: <Calendar className="w-8 h-8" />,
                title: "Monthly Rate",
                price: "$850",
                period: "/month",
                savings: "Save over $650 vs nightly!",
                features: ["30+ night stays", "Best value rate", "Priority housekeeping", "Flexible check-out"],
                highlight: true,
              },
              {
                icon: <Briefcase className="w-8 h-8" />,
                title: "Corporate Rate",
                price: "Custom",
                period: "pricing",
                savings: "Special rates for businesses",
                features: ["Volume discounts", "Direct billing", "Dedicated support", "Multiple rooms"],
                highlight: false,
              },
            ].map((offer, index) => (
              <div
                key={index}
                className={cn(
                  "relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 animate-fade-in group",
                  offer.highlight
                    ? "bg-harbor-blue text-white shadow-card-hover"
                    : "bg-card shadow-card hover:shadow-card-hover"
                )}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {offer.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-harbor-blue text-white text-sm font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className={cn(
                  "w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300",
                  offer.highlight
                    ? "bg-white/20 text-warm-gold"
                    : "bg-warm-gold/10 text-warm-gold group-hover:bg-warm-gold group-hover:text-white"
                )}>
                  {offer.icon}
                </div>
                <h3 className={cn(
                  "font-serif text-xl font-semibold mb-2",
                  offer.highlight ? "text-white" : "text-harbor-blue"
                )}>
                  {offer.title}
                </h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className={cn(
                    "font-serif text-4xl font-bold",
                    offer.highlight ? "text-white" : "text-harbor-blue"
                  )}>
                    {offer.price}
                  </span>
                  <span className={offer.highlight ? "text-white/70" : "text-muted-foreground"}>
                    {offer.period}
                  </span>
                </div>
                <p className={cn(
                  "text-sm font-medium mb-6",
                  offer.highlight ? "text-warm-gold" : "text-warm-gold-dark"
                )}>
                  {offer.savings}
                </p>
                <ul className="space-y-3">
                  {offer.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 className={cn(
                        "w-5 h-5 flex-shrink-0",
                        offer.highlight ? "text-warm-gold" : "text-warm-gold"
                      )} />
                      <span className={cn(
                        "text-sm",
                        offer.highlight ? "text-white/90" : "text-muted-foreground"
                      )}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  variant={offer.highlight ? "hero" : "outline"}
                  className="w-full mt-8"
                >
                  <Link to="/booking">
                    {offer.title === "Corporate Rate" ? "Contact Us" : "Book Now"}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="bg-warm-gold/15 text-harbor-blue border-warm-gold/30 mb-4">
                <HelpCircle className="w-4 h-4 mr-2" />
                FAQ
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-harbor-blue mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Everything you need to know about your extended stay at Harbor Heights.
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: "What is included in the room rate?",
                  answer: "Your rate includes all utilities, high-speed Wi-Fi, cable TV, weekly housekeeping, free parking, and access to all on-site amenities like our fitness center. There are no hidden fees or resort charges.",
                },
                {
                  question: "What does the kitchenette include?",
                  answer: "Each room features a full kitchenette with a microwave, full-size refrigerator, sink with cabinet storage, and basic cookware. It's perfect for preparing simple meals and saving money on dining out during your extended stay.",
                },
                {
                  question: "What is your cancellation policy?",
                  answer: "We offer flexible cancellation up to 48 hours before check-in for a full refund on standard bookings. For extended stays of 4 weeks or more, contact us to discuss customized terms that work for your situation.",
                },
                {
                  question: "Are pets allowed?",
                  answer: "Yes, we're pet-friendly! We welcome dogs and cats with a one-time pet fee. Service animals are always welcome at no additional charge. Contact us for details on our pet policy.",
                },
                {
                  question: "Can I extend my stay?",
                  answer: "Absolutely! Many of our guests extend their stays. Simply contact our front desk and we'll work with you to extend your reservation based on availability. Longer stays may qualify for additional discounts.",
                },
                {
                  question: "Is parking available?",
                  answer: "Yes, complimentary on-site parking is included with all suite bookings. No additional fees or permits required.",
                },
                {
                  question: "What are your check-in and check-out times?",
                  answer: "Standard check-in is at 3:00 PM and check-out is at 11:00 AM. Early check-in or late check-out may be available upon request based on availability. Contact us in advance to arrange.",
                },
                {
                  question: "Do you offer corporate or group rates?",
                  answer: "Yes! We offer special rates for businesses, contractors, and groups needing multiple rooms. Contact our team for customized pricing, direct billing options, and dedicated support for your company's housing needs.",
                },
              ].map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-card rounded-xl px-6 shadow-card border-none"
                >
                  <AccordionTrigger className="text-left font-semibold text-harbor-blue hover:text-warm-gold hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-4">Still have questions?</p>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Contact Our Team</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-28 bg-harbor-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">
              Ready to Book Your Extended Stay?
            </h2>
            <p className="text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
              Experience comfort, convenience, and value. Rooms starting at just <span className="font-bold text-warm-gold">$50/night</span> or <span className="font-bold text-warm-gold">$225/week</span> with no hidden fees.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button asChild variant="hero" size="xl">
                <Link to="/booking">Book Your Extended Stay</Link>
              </Button>
              <a
                href="tel:+1-712-224-7003"
                className="flex items-center gap-2 text-white font-semibold hover:text-warm-gold transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>Or call (712) 224-7003</span>
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-6 pt-8 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-warm-gold" />
                <span>No Resort Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-warm-gold" />
                <span>Flexible Cancellation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-warm-gold" />
                <span>Best Rate Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
